import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  DesignV2Scope,
  EmptyState,
  Skeleton,
} from "../design-v2";
import {
  CardioCard,
  ExerciseCard,
  MetricTile,
  MuscleDistribution,
  StackHeader,
  StickyStackedNav,
  SupersetGroup,
  UnsupportedBlockCard,
} from "../components/product";
import { useMultiWeekProgram } from "../context/MultiWeekProgramContext";
import { getCycleBuilderPath } from "../features/multiWeek/routes";
import {
  getCycleDetails,
  openOrCreateCycleEditDraft,
} from "../services/api";
import { computeWorkoutMetrics } from "../utils/workoutMetrics";
import { usePlannedMuscleVolume } from "../features/muscleVolume/usePlannedMuscleVolume";
import "./cycleProgramDetails.css";

function formatDate(value) {
  if (!value) {
    return "Not set";
  }

  return new Date(`${value}T00:00:00`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatStatus(value) {
  const normalized = String(value || "published").trim().toLowerCase();
  return normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : "Published";
}

function selectionsMatch(left, right) {
  return left != null && right != null && String(left) === String(right);
}

function GearIcon() {
  return (
    <svg className="lz-cycle-viewer__gear-icon" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="3.25" />
      <circle cx="12" cy="12" r="7.25" />
      <path d="M12 2.5v2.25M12 19.25v2.25M21.5 12h-2.25M4.75 12H2.5M18.7 5.3l-1.6 1.6M6.9 17.1l-1.6 1.6M18.7 18.7l-1.6-1.6M6.9 6.9 5.3 5.3" />
    </svg>
  );
}

function BoltIcon() {
  return (
    <svg className="lz-cycle-viewer__bolt-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M13.5 2.5 5.5 13h5.25L9.5 21.5l9-11.5h-5.75l.75-7.5Z" />
    </svg>
  );
}

function getPublishedWeeks(response) {
  const weeks = response?.builderPayload?.weeks;
  return Array.isArray(weeks) ? weeks : [];
}

function getInitialSelections(response) {
  const weeks = getPublishedWeeks(response);
  const requestedWeek = response?.builderPayload?.selectedWeek;
  const week = weeks.find((item) => selectionsMatch(item.weekNumber, requestedWeek)) || weeks[0] || null;
  const workouts = Array.isArray(week?.workouts) ? week.workouts : [];

  return {
    weekNumber: week?.weekNumber ?? null,
    workoutOrderIndex: workouts[0]?.orderIndex ?? null,
  };
}

function CycleViewerLoading() {
  return (
    <DesignV2Scope
      className="lz-cycle-viewer lz-cycle-viewer--loading"
      aria-busy="true"
      aria-label="Loading cycle viewer"
    >
      <header className="lz-cycle-viewer__loading-header">
        <Skeleton shape="circle" width={44} aria-label="Loading back control" />
        <Skeleton shape="text" width="55%" aria-label="Loading cycle title" />
        <Skeleton shape="circle" width={44} aria-label="Loading cycle action" />
      </header>
      <div className="lz-cycle-viewer__loading-nav">
        <Skeleton shape="block" height={44} aria-label="Loading week navigation" />
        <Skeleton shape="block" height={56} aria-label="Loading day navigation" />
      </div>
      <main className="lz-cycle-viewer__content">
        <div className="lz-cycle-viewer__loading-hero">
          <Skeleton shape="text" width="35%" aria-label="Loading workout position" />
          <Skeleton shape="text" width="72%" aria-label="Loading workout title" />
          <Skeleton shape="text" width="90%" aria-label="Loading workout description" />
          <Skeleton shape="block" height={56} aria-label="Loading training action" />
        </div>
        <div className="lz-cycle-viewer__loading-metrics">
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton
              shape="block"
              height={72}
              aria-label={`Loading metric ${index + 1}`}
              key={index}
            />
          ))}
        </div>
        <Skeleton shape="block" height={120} aria-label="Loading muscle distribution" />
        <Skeleton shape="block" height={240} aria-label="Loading exercise card" />
      </main>
    </DesignV2Scope>
  );
}

const TUT_HELP = "Time Under Tension estimates how long your muscles are working during the planned sets. It adds context to reps and tempo, but is not a standalone measure of muscle growth.";

function BlockRenderer({ block, position, presentationFallbackIntensity }) {
  if (block.type === "single") {
    return (
      <ExerciseCard
        block={block}
        position={position}
        presentationFallbackIntensity={presentationFallbackIntensity}
      />
    );
  }
  if (block.type === "superset") {
    return (
      <SupersetGroup
        block={block}
        position={position}
        presentationFallbackIntensity={presentationFallbackIntensity}
      />
    );
  }
  if (block.type === "cardio") {
    return <CardioCard block={block} position={position} />;
  }
  return <UnsupportedBlockCard block={block} position={position} />;
}

export default function CycleProgramDetails() {
  const navigate = useNavigate();
  const { cycleId } = useParams();
  const { draftMetadata, hydrateProgramDraft, beginHydrationTarget } = useMultiWeekProgram();
  const [details, setDetails] = useState(null);
  const [selectedWeekNumber, setSelectedWeekNumber] = useState(null);
  const [selectedWorkoutOrderIndex, setSelectedWorkoutOrderIndex] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);
  const [actionError, setActionError] = useState("");
  const [isOpeningDraft, setIsOpeningDraft] = useState(false);
  const loadRequestRef = useRef({ key: null, promise: null });

  useEffect(() => {
    let isMounted = true;
    const requestKey = `${cycleId}:${retryKey}`;
    let request = loadRequestRef.current.promise;

    if (loadRequestRef.current.key !== requestKey || !request) {
      request = getCycleDetails(cycleId);
      loadRequestRef.current = { key: requestKey, promise: request };
    }

    async function loadDetails() {
      setIsLoading(true);
      setLoadError(null);
      setActionError("");

      try {
        const response = await request;
        if (isMounted) {
          const initialSelections = getInitialSelections(response);
          setDetails(response);
          setSelectedWeekNumber(initialSelections.weekNumber);
          setSelectedWorkoutOrderIndex(initialSelections.workoutOrderIndex);
        }
      } catch (error) {
        if (isMounted) {
          setDetails(null);
          setSelectedWeekNumber(null);
          setSelectedWorkoutOrderIndex(null);
          setLoadError(error);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadDetails();

    return () => {
      isMounted = false;
    };
  }, [cycleId, retryKey]);

  const publishedWeeks = useMemo(() => getPublishedWeeks(details), [details]);
  const selectedWeek = useMemo(
    () => publishedWeeks.find(
      (week) => selectionsMatch(week.weekNumber, selectedWeekNumber)
    ) || null,
    [publishedWeeks, selectedWeekNumber]
  );
  const selectedWorkouts = useMemo(
    () => Array.isArray(selectedWeek?.workouts) ? selectedWeek.workouts : [],
    [selectedWeek]
  );
  const selectedWorkout = useMemo(
    () => selectedWorkouts.find(
      (workout) => selectionsMatch(workout.orderIndex, selectedWorkoutOrderIndex)
    ) || null,
    [selectedWorkoutOrderIndex, selectedWorkouts]
  );
  const selectedBlocks = useMemo(
    () => Array.isArray(selectedWorkout?.blocks) ? selectedWorkout.blocks : [],
    [selectedWorkout]
  );
  const metrics = useMemo(
    () => selectedWorkout ? computeWorkoutMetrics(selectedWorkout) : null,
    [selectedWorkout]
  );
  const volumeWorkouts = useMemo(
    () => (selectedWorkout ? [selectedWorkout] : []),
    [selectedWorkout]
  );
  const muscleVolume = usePlannedMuscleVolume(volumeWorkouts);

  const handleWeekSelect = (weekNumber) => {
    const nextWeek = publishedWeeks.find(
      (week) => selectionsMatch(week.weekNumber, weekNumber)
    );
    if (!nextWeek) {
      return;
    }

    const nextWorkouts = Array.isArray(nextWeek.workouts) ? nextWeek.workouts : [];
    setSelectedWeekNumber(nextWeek.weekNumber);
    setSelectedWorkoutOrderIndex((currentOrderIndex) => {
      const remainsValid = nextWorkouts.some(
        (workout) => selectionsMatch(workout.orderIndex, currentOrderIndex)
      );
      return remainsValid ? currentOrderIndex : nextWorkouts[0]?.orderIndex ?? null;
    });
  };

  const handleEdit = async () => {
    if (
      draftMetadata.loadedFromBackend &&
      draftMetadata.cycleId === cycleId &&
      draftMetadata.status === "draft"
    ) {
      // Already loaded (e.g. re-clicking Edit on the cycle currently open in
      // the builder) -- reuse it instead of an unconditional re-fetch that
      // could force-hydrate over in-flight/dirty local edits.
      navigate(getCycleBuilderPath(cycleId));
      return;
    }

    setIsOpeningDraft(true);
    setActionError("");

    try {
      await beginHydrationTarget({ cycleId, planId: null });
      const response = await openOrCreateCycleEditDraft(cycleId);
      hydrateProgramDraft(response);
      navigate(getCycleBuilderPath(cycleId));
    } catch (openError) {
      setActionError(openError.message || "Unable to open draft.");
    } finally {
      setIsOpeningDraft(false);
    }
  };

  if (isLoading) {
    return <CycleViewerLoading />;
  }

  if (!details) {
    const notFound = loadError?.status === 404;
    return (
      <DesignV2Scope className="lz-cycle-viewer">
        <StackHeader title="Program" onBack={() => navigate("/program")} />
        <main className="lz-cycle-viewer__content">
          <EmptyState
            title={notFound ? "Program not found" : "Unable to load program"}
            body={notFound
              ? "This cycle may no longer be available."
              : loadError?.message || "The published program could not be loaded."}
            action={notFound ? (
              <Button onClick={() => navigate("/program")}>Back to Program</Button>
            ) : (
              <Button onClick={() => setRetryKey((value) => value + 1)}>Retry</Button>
            )}
          />
        </main>
      </DesignV2Scope>
    );
  }

  const cycle = details.cycle;
  const summaryWeeks = Number(cycle.durationWeeks) || publishedWeeks.length;
  const remainingWeeks = Math.max(0, Number(cycle.remainingWeeks) || 0);
  const hasPublishedPayload = details.builderPayload !== null;
  const hasUsableDraft = Boolean(details.draftState?.hasUsableDraft);
  const presentationFallbackIntensity =
    details.viewerContext?.experienceLevel === "beginner"
      ? null
      : { mode: "rir", value: 2 };

  return (
    <DesignV2Scope className="lz-cycle-viewer">
      <StackHeader
        title={cycle.name}
        onBack={() => navigate("/program")}
        trailing={(
          <div className="lz-cycle-viewer__header-actions">
            <Button
              variant="secondary"
              size="icon"
              className="lz-cycle-viewer__edit-action"
              aria-label={cycle.temporalStatus === "past" ? "Past program" : "Edit program"}
              title={cycle.temporalStatus === "past"
                ? "Past programs cannot be edited"
                : hasUsableDraft
                  ? "Edit program. Usable draft available"
                  : "Edit program"}
              onClick={handleEdit}
              isLoading={isOpeningDraft}
              loadingLabel="Opening draft..."
              disabled={cycle.temporalStatus === "past"}
            >
              <GearIcon />
              {hasUsableDraft ? (
                <span className="lz-cycle-viewer__draft-dot" aria-hidden="true" />
              ) : null}
            </Button>
          </div>
        )}
      />

      {hasPublishedPayload && publishedWeeks.length > 0 ? (
        <StickyStackedNav
          weeks={publishedWeeks}
          selectedWeekNumber={selectedWeekNumber}
          onSelectWeek={handleWeekSelect}
          workouts={selectedWorkouts}
          selectedWorkoutOrderIndex={selectedWorkoutOrderIndex}
          onSelectWorkout={setSelectedWorkoutOrderIndex}
        />
      ) : null}

      <main className="lz-cycle-viewer__content">
        {actionError ? <p className="lz-cycle-viewer__action-error" role="alert">{actionError}</p> : null}

        {!hasPublishedPayload ? (
          <EmptyState
            title="Program not published yet"
            body="This cycle has no published plan to view. You can return when a draft has been published."
            action={(
              <Button
                variant="secondary"
                className="lz-cycle-viewer__edit-draft-action"
                onClick={handleEdit}
                isLoading={isOpeningDraft}
                loadingLabel="Opening draft..."
                disabled={cycle.temporalStatus === "past"}
              >
                Edit draft
              </Button>
            )}
          />
        ) : publishedWeeks.length === 0 ? (
          <EmptyState
            title="No published weeks available"
            body="The published program does not contain any weeks to display."
          />
        ) : selectedWorkouts.length === 0 ? (
          <EmptyState
            title="No sessions scheduled this week"
            body={`Week ${selectedWeek?.weekNumber} is a valid recovery week with no scheduled workouts.`}
          />
        ) : selectedWorkout && metrics ? (
          <>
            <section className="lz-cycle-viewer__workout-heading">
              <p>Week {selectedWeek.weekNumber} · Day {selectedWorkout.orderIndex}</p>
              <h2 className="lz-cycle-viewer__workout-title">{selectedWorkout.name}</h2>
              {selectedWorkout.notes ? <p>{selectedWorkout.notes}</p> : null}
              <Button className="lz-cycle-viewer__start" size="lg" disabled>
                Start training
                <BoltIcon />
              </Button>
            </section>

            <div
              className="lz-product-metrics lz-product-metrics--section lz-product-metrics--aligned lz-cycle-viewer__metrics"
              role="region"
              aria-label="Workout metrics"
            >
              <MetricTile
                label="Exercises"
                value={metrics.exerciseCount}
                barValue={metrics.exerciseCount}
                barMax={12}
              />
              <MetricTile
                label="Sets"
                value={metrics.setCount}
                barValue={metrics.setCount}
                barMax={32}
              />
              <MetricTile
                label="Duration"
                value={`${metrics.estimatedDurationMinutes}m`}
                barValue={metrics.estimatedDurationMinutes}
                barMax={120}
              />
              <MetricTile
                label="TUT"
                value={`${metrics.totalTUTMinutes}m`}
                barValue={metrics.totalTUTMinutes}
                barMax={60}
                helpTitle="Time Under Tension"
                helpBody={TUT_HELP}
              />
            </div>

            <MuscleDistribution
              items={muscleVolume.items}
              weighted
              unclassifiedSets={muscleVolume.unclassifiedSets}
              status={muscleVolume.status}
            />

            <section className="lz-cycle-viewer__program" aria-labelledby="cycle-viewer-program-title">
              <h2 className="lz-product-section-title" id="cycle-viewer-program-title">Program</h2>
              {selectedBlocks.length > 0 ? (
                <div className="lz-cycle-viewer__blocks">
                  {selectedBlocks.map((block, index) => (
                    <BlockRenderer
                      block={block}
                      position={index + 1}
                      presentationFallbackIntensity={presentationFallbackIntensity}
                      key={block.id || `${block.type}-${index}`}
                    />
                  ))}
                </div>
              ) : (
                <EmptyState
                  title="No exercises scheduled"
                  body="This workout does not contain any published exercise blocks."
                />
              )}
            </section>
          </>
        ) : null}

        <section className="lz-cycle-viewer__cycle-actions" aria-labelledby="cycle-details-title">
          <div className="lz-cycle-viewer__cycle-details">
            <h2 id="cycle-details-title">Cycle details</h2>
            <dl>
              <div>
                <dt>Status</dt>
                <dd>{formatStatus(cycle.temporalStatus)}</dd>
              </div>
              <div>
                <dt>Dates</dt>
                <dd>{formatDate(cycle.startDate)} – {formatDate(cycle.endDate)}</dd>
              </div>
              <div>
                <dt>Length</dt>
                <dd>{summaryWeeks} weeks</dd>
              </div>
              <div>
                <dt>Remaining</dt>
                <dd>{remainingWeeks} {remainingWeeks === 1 ? "week" : "weeks"}</dd>
              </div>
            </dl>
          </div>
        </section>
      </main>
    </DesignV2Scope>
  );
}
